import { createContext, useContext, type ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';
import type { Repository } from '../data/repository';
import { DemoRepository } from '../data/demo/demoRepository';
import { SupabaseRepository } from '../data/supabase/supabaseRepository';
import { DemoAuthService, SupabaseAuthService, type AuthService } from '../auth/authService';
import type { AppConfig } from '../config';

export interface Services {
  mode: 'demo' | 'supabase';
  repo: Repository;
  auth: AuthService;
}

export function createServices(cfg: AppConfig): Services {
  if (cfg.mode === 'supabase' && cfg.supabaseUrl && cfg.supabasePublishableKey) {
    const sb = createClient(cfg.supabaseUrl, cfg.supabasePublishableKey, {
      auth: { persistSession: true, detectSessionInUrl: true, flowType: 'pkce' },
    });
    return { mode: 'supabase', repo: new SupabaseRepository(sb), auth: new SupabaseAuthService(sb) };
  }
  return { mode: 'demo', repo: new DemoRepository(), auth: new DemoAuthService() };
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const s = useContext(ServicesContext);
  if (!s) throw new Error('ServicesProvider ontbreekt');
  return s;
}

export const useRepo = () => useServices().repo;
